import React, { useMemo, useState } from 'react';
import {
  Package,
  DownloadCloud,
  UploadCloud,
  AlertOctagon,
  Layers,
  BarChart2
} from 'lucide-react';
import DataTable from './DataTable';
import StatCard from './StatCard';
import Button from './common/Button';
import { useAppContext } from '../context/AppContext';
import { formatCurrency } from '../utils/helpers';
import EmptyState from './common/EmptyState';
import { t } from '../i18n';

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length <= 1) return [];
  const headers = lines[0].split(',').map((header) => header.trim());
  return lines.slice(1).map((line) => {
    const cells = line.split(',').map((cell) => cell.trim());
    return headers.reduce((acc, header, index) => {
      acc[header] = cells[index] || '';
      return acc;
    }, {});
  });
};

const Inventory = () => {
  const {
    inventory,
    locations,
    movements,
    importMaterialsFromCsv
  } = useAppContext();

  if (!locations || locations.length === 0) {
    return (
      <EmptyState
        title={t('Inventario: faltan ubicaciones')}
        description={t(
          'Para controlar inventario necesitas al menos una ubicación (bodega central, proyecto, patio, etc.).'
        )}
        checklist={['Crear ubicaciones base en Administración o cargar templates.']}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
      />
    );
  }

  if (!inventory || inventory.length === 0) {
    return (
      <EmptyState
        title={t('Inventario: está vacío')}
        description={t(
          'Aún no hay materiales/stock. Puedes cargar por CSV o generar entradas desde recepciones de OC (líneas con SKU).'
        )}
        checklist={[
          'Cargar materiales por CSV (SKU, descripción, categoría, valor unitario, ubicaciones).',
          'Recepcionar una OC con líneas que tengan SKU para aumentar stock.'
        ]}
        primaryAction={{ label: 'Ir a Órdenes de compra', pageId: 'procurement-orders' }}
        secondaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
      />
    );
  }
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);

  const itemsWithAggregates = useMemo(() => {
    return inventory.map((item) => {
      const totalQuantity = item.locations.reduce((sum, location) => sum + location.quantity, 0);
      const totalValue = item.locations.reduce(
        (sum, location) => sum + location.quantity * item.unitValue,
        0
      );
      return {
        ...item,
        totalQuantity,
        totalValue
      };
    });
  }, [inventory]);

  const inventoryData = useMemo(() => {
    const baseData = selectedLocation === 'all'
      ? itemsWithAggregates
      : inventory
          .map((item) => {
            const locationStock = item.locations.find(
              (location) => location.locationId === selectedLocation
            );
            return {
              ...item,
              quantity: locationStock ? locationStock.quantity : 0,
              totalValue: locationStock ? locationStock.quantity * item.unitValue : 0
            };
          })
          .filter((item) => item.quantity > 0);

    if (selectedCategory === 'all') return baseData;
    return baseData.filter((item) => item.category === selectedCategory);
  }, [itemsWithAggregates, inventory, selectedLocation, selectedCategory]);

  const categoryOptions = useMemo(() => {
    const unique = new Set(inventory.map((item) => item.category).filter(Boolean));
    return Array.from(unique).sort();
  }, [inventory]);

  const skuCount = inventory.length;
  const totalUnits = itemsWithAggregates.reduce((sum, item) => sum + item.totalQuantity, 0);
  const totalValue = itemsWithAggregates.reduce((sum, item) => sum + item.totalValue, 0);
  const belowSafetyStock = itemsWithAggregates.filter(
    (item) => item.totalQuantity < item.safetyStock
  ).length;

  const columns = useMemo(() => {
    const baseColumns = [
      { key: 'sku', label: 'SKU', sortable: true },
      {
        key: 'description',
        label: t('Descripción'),
        sortable: true
      },
      {
        key: 'category',
        label: t('Categoría'),
        sortable: true,
        render: (category) => category
      },
      {
        key: selectedLocation === 'all' ? 'totalQuantity' : 'quantity',
        label: t('Stock'),
        sortable: true,
        align: 'right',
        render: (quantity, row) => (
          <span>
            <span className="font-semibold text-slate-800">{t(quantity)}</span>
            <span className="text-xs text-slate-400 ml-1">{t(row.unit)}</span>
          </span>
        )
      },
      {
        key: 'unitValue',
        label: t('Valor Unitario'),
        sortable: true,
        align: 'right',
        render: (value) => formatCurrency(value)
      },
      {
        key: selectedLocation === 'all' ? 'totalValue' : 'totalValue',
        label: t('Valor total'),
        sortable: true,
        align: 'right',
        render: (value) => (
          <span className="font-semibold text-neutral-900">{formatCurrency(value)}</span>
        )
      },
      {
        key: 'safetyStock',
        label: t('Stock Seguridad'),
        sortable: true,
        align: 'right',
        render: (value, row) => {
          const currentQuantity =
            selectedLocation === 'all' ? row.totalQuantity : row.quantity ?? 0;
          const isBelow = currentQuantity < value;
          return (
            <span className={isBelow ? 'text-danger-600 font-semibold' : 'text-slate-700'}>
              {t(value)} {isBelow ? t('(Bajo)') : ''}
            </span>
          );
        }
      }
    ];

    if (selectedLocation === 'all') {
      baseColumns.splice(3, 0, {
        key: 'locations',
        label: t('Ubicaciones'),
        render: (_, row) => (
          <div className="flex flex-wrap gap-2">
            {row.locations.map((location) => {
              const locationInfo = locations.find((loc) => loc.id === location.locationId);
              return (
                <span
                  key={`${row.sku}-${location.locationId}`}
                  className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs text-slate-500"
                >
                  {t(locationInfo?.code) || 'LOC'}· {t(location.quantity)}
                </span>
              );
            })}
          </div>
        )
      });
    } else {
      baseColumns.splice(3, 0, {
        key: 'locationName',
        label: t('Ubicación'),
        render: () => locations.find((location) => location.id === selectedLocation)?.name || '-'
      });
    }

    return baseColumns;
  }, [locations, selectedLocation]);

  const handleExport = () => {
    const headers = ['SKU', 'Descripción', 'Categoría', 'Unidad', 'Cantidad', 'Valor Unitario', 'Valor Total'];
    const rows = inventoryData.map((item) => [
      item.sku,
      item.description,
      item.category,
      item.unit,
      selectedLocation === 'all' ? item.totalQuantity : item.quantity,
      item.unitValue,
      selectedLocation === 'all' ? item.totalValue : item.totalValue
    ]);
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventario_alcor.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const text = loadEvent.target?.result;
      if (typeof text === 'string') {
        const rows = parseCsv(text);
        if (rows.length) {
          importMaterialsFromCsv(rows);
        }
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const filteredMovements = useMemo(() => {
    if (!selectedItem) return [];
    return movements
      .filter((movement) => movement.sku === selectedItem.sku)
      .slice()
      .sort((a, b) => (a.date > b.date ? -1 : 1));
  }, [movements, selectedItem]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Inventario y Trazabilidad')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Control centralizado por SKU, locación, stock de seguridad y movimientos históricos.'
          )}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 h-8 px-3 border border-neutral-300 rounded text-sm font-medium text-neutral-700 cursor-pointer hover:bg-neutral-50 transition-colors">
            <UploadCloud className="w-3.5 h-3.5" />{t('Carga Masiva CSV')}<input type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
          </label>
          <Button variant="primary" icon={DownloadCloud} onClick={handleExport}>{t('Exportar Excel')}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title={t('SKU registrados')}
          value={skuCount.toString()}
          subtitle={t('Materiales y servicios controlados')}
          icon={Package}
        />
        <StatCard
          title={t('Stock disponible')}
          value={totalUnits.toString()}
          subtitle={t('Unidades en todas las ubicaciones')}
          icon={Layers}
        />
        <StatCard
          title={t('Valor inventario')}
          value={formatCurrency(totalValue)}
          subtitle={t('Valorizado a costo unitario')}
          icon={BarChart2}
        />
        <StatCard
          title={t('Bajo stock de seguridad')}
          value={belowSafetyStock.toString()}
          subtitle={t('SKU que requieren reposición')}
          icon={AlertOctagon}
        />
      </div>

      <div className="bg-white border border-slate-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">{t('Filtros')}</h3>
            <p className="text-xs text-slate-500">{t('Cambia locación y categoría para filtrar el inventario.')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-500">{t('Ubicación')}</label>
              <select
                value={selectedLocation}
                onChange={(event) => setSelectedLocation(event.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-none text-sm"
              >
                <option value="all">{t('Todas')}</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {t(location.name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-500">{t('Categoría')}</label>
              <select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-none text-sm"
              >
                <option value="all">{t('Todas')}</option>
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {t(category)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <DataTable
        data={inventoryData}
        columns={columns}
        searchPlaceholder={t('Buscar por SKU, descripción, categoría...')}
        onRowClick={setSelectedItem}
        variant="classic"
        tableMaxHeight="60vh"
      />

      {selectedItem && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 flex justify-end">
          <div className="w-full max-w-xl h-full bg-white border-l border-slate-100 p-8 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs text-slate-400 uppercase">{t('Detalle de inventario')}</p>
                  <h3 className="text-xl font-bold text-slate-900">{t(selectedItem.description)}</h3>
                  <p className="text-xs text-slate-500">{t('SKU')} {t(selectedItem.sku)}· {t(selectedItem.category)}</p>
                </div>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="text-sm text-slate-500 hover:text-slate-900 transition"
                >{t('cerrar')}</button>
              </div>

              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                    <p className="text-xs text-slate-400 uppercase">{t('Stock total')}</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">{t(selectedItem.totalQuantity)} {t(selectedItem.unit)}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                    <p className="text-xs text-slate-400 uppercase">{t('Valor total')}</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">{formatCurrency(selectedItem.totalValue)}</p>
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-700">{t('Stock por ubicación')}</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {selectedItem.locations.map((location) => {
                      const locationInfo = locations.find((loc) => loc.id === location.locationId);
                      return (
                        <div key={location.locationId} className="px-4 py-3 flex items-center justify-between text-sm text-slate-600">
                          <div>
                            <p className="font-semibold text-slate-800">{t(locationInfo?.name)}</p>
                            <p className="text-xs text-slate-400">{t(locationInfo?.code)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-slate-800">{t(location.quantity)} {t(selectedItem.unit)}</p>
                            <p className="text-xs text-slate-400">
                              {formatCurrency(location.quantity * selectedItem.unitValue)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700">{t('Movimientos recientes')}</p>
                    <span className="text-xs text-slate-400">{t(filteredMovements.length)} {t('registros')}</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {filteredMovements.map((movement) => (
                      <div
                        key={movement.id}
                        className="px-4 py-3 flex items-center justify-between text-sm text-slate-600"
                      >
                        <div>
                          <p className="font-semibold text-slate-800">
                            {t(movement.type)} · {t(movement.reference)}
                          </p>
                          <p className="text-xs text-slate-400">
                            {t(movement.date)} · {t(locations.find((loc) => loc.id === movement.locationId)?.name)}
                          </p>
                        </div>
                        <span className="font-semibold text-slate-800">{t(movement.quantity)}</span>
                      </div>
                    ))}
                    {filteredMovements.length === 0 && (
                      <div className="px-4 py-5 text-sm text-slate-400 text-center">{t('Sin movimientos registrados para este SKU.')}</div>
                    )}
                  </div>
                </div>
              </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
